// Carlyle Avenue — curated predictive-search "discovery" surface behaviour (additive).
//
// Renders inside <predictive-search-component> via snippets/predictive-search-discovery.liquid.
// This layer adds ONLY the discovery interactions and deliberately does NOT fork
// predictive-search.js (same convention as ca-search-a11y.js):
//
//   1. Category tabs  — a real WAI-ARIA tablist (roving tabindex, arrow/Home/End nav)
//                        that swaps which curated panel is visible. Pure client-side.
//   2. Pills          — inject the pill's term into the base search input and dispatch a
//                        real InputEvent so the base component's own debounced search()
//                        runs unmodified (it ignores events without `inputType`, see
//                        predictive-search.js#search). This is the clean handoff.
//   3. Tiles          — plain <a> links; native navigation, nothing to wire here.
//
// The element is re-created/re-connected whenever the base morph()s the empty state back
// in, so all wiring lives in connectedCallback and is guarded against double-binding.

import { nextPill } from '@theme/ca-variant-pills';

const ACTIVATE_KEYS = ['Enter', ' ', 'Spacebar'];

class CaSearchDiscovery extends HTMLElement {
  #wired = false;

  connectedCallback() {
    if (this.#wired) return;
    this.#wired = true;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /** @param {HTMLElement} tab */
  #activateTab(tab) {
    const key = tab.dataset.caSdTab;
    if (!key) return;

    for (const t of this.#tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      const match = panel instanceof HTMLElement && panel.dataset.caSdPanel === key;
      panel.toggleAttribute('hidden', !match);
    }
  }

  /**
   * Inject a term into the base predictive-search input and trigger its own search flow.
   * @param {string} term
   */
  #injectQuery(term) {
    const root = this.closest('predictive-search-component');
    const input = /** @type {HTMLInputElement | null} */ (root?.querySelector('input[role="combobox"]'));
    if (!input) return;

    input.value = term;
    input.focus();
    // `inputType` must be truthy or the base search() bails (predictive-search.js#search).
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: term }));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      event.preventDefault();
      this.#activateTab(tab);
      tab.focus();
      return;
    }

    const pill = target.closest('[data-ca-sd-term]');
    if (pill instanceof HTMLElement && this.contains(pill)) {
      event.preventDefault();
      const term = pill.dataset.caSdTerm;
      if (term) this.#injectQuery(term);
    }
    // Tiles are anchors — let the browser navigate.
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Tablist owns keyboard nav while a tab is focused — keep the base predictive-search
    // arrow/Enter handlers (bound on the form) from also acting on these keys.
    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      const next = nextPill(event.key, this.#tabs, tab);
      if (next) {
        event.preventDefault();
        event.stopPropagation();
        this.#activateTab(next);
        next.focus();
      } else if (ACTIVATE_KEYS.includes(event.key)) {
        // Let the native button activation fire (→ click → #activateTab); just keep the
        // event from bubbling to the base form's Enter-submits-search handler.
        event.stopPropagation();
      }
      return;
    }

    // Pills: keep Enter/Space from reaching the base "Enter submits empty query" handler;
    // the native button click then injects the term.
    const pill = target.closest('[data-ca-sd-term]');
    if (pill instanceof HTMLElement && this.contains(pill) && ACTIVATE_KEYS.includes(event.key)) {
      event.stopPropagation();
    }
  };
}

if (!customElements.get('ca-search-discovery')) {
  customElements.define('ca-search-discovery', CaSearchDiscovery);
}
